import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import dayjs from '~/timeInit';

import { Alert, Box, Button, Stack, TextField } from '@mui/material';
import type { DateRange } from '@mui/x-date-pickers-pro';

import SearchBigQueryDateRangePicker, {
  getDefaultSearchDateRange,
  toBigQueryExclusiveEndDate,
} from '~/components/Content/SearchAnalytics/SearchBigQueryDateRangePicker';

import { useGetPublicOffersByIds } from '~/hooks/apis/public-offer/useGetPublicOffersByIds';
import { useReproduceSearch } from '~/hooks/apis/reproduce-search/useReproduceSearch';
import { useReproduceSearchImpressions } from '~/hooks/apis/reproduce-search/useReproduceSearchImpressions';
import type { PlaceGeom, PlaceNameLookup } from '~/hooks/apis/search/useGetPlaces';
import { useGetPlaces } from '~/hooks/apis/search/useGetPlaces';

import type { ReproduceSearchParams, SearchReproductionRow } from '~/services/ReproduceSearchService';
import type { Dayjs } from '~/services/TimeService';

import { filterLePlaceIds, googleBoundsUrlValueToPolygonGeom, isGoogleBoundsUrlValue } from '~/utils/lePlaceId';

import ImpressionsSection from './ImpressionsSection';
import MatchedPlacesMap from './MatchedPlacesMap';
import SearchDetailSection, { type SearchDetailField } from './SearchDetailSection';
import SectionLoader from './SectionLoader';
import { buildEntityFallbackPlaces, buildPropertyMapPlaces } from './entityMapPlaces';
import { buildFilterFields, buildMatchFields, formatCellValue } from './searchMatchFields';

const SEARCH_ID_FIELD_WIDTH = 335;
const DATE_INPUT_WIDTH = 140;
const DEFAULT_OFFER_LOOKUP_REGION = 'AU';

function buildSubmittedParams(searchIdValue: string, start: Dayjs, end: Dayjs): ReproduceSearchParams {
  return {
    startDate: start.format('YYYY-MM-DD'),
    endDate: toBigQueryExclusiveEndDate(end),
    searchId: searchIdValue.trim(),
  };
}

function formatFlexibleMonths(isoMonths: Array<string>) {
  if (!isoMonths.length) {
    return '';
  }

  return isoMonths.map((isoMonth) => dayjs(`${isoMonth}-01`).format('MMM YYYY')).join(', ');
}

function formatOccupancy(adults: number | null, children: number | null) {
  const parts: Array<string> = [];

  if (adults != null) {
    parts.push(`${adults}A`);
  }

  if (children != null) {
    parts.push(`${children}C`);
  }

  return parts.join(', ');
}

function formatUtm(source: string | null, medium: string | null, campaign: string | null) {
  return [source, medium, campaign].filter(Boolean).join(' / ');
}

function buildMetaFields(row: SearchReproductionRow): Array<SearchDetailField> {
  return [
    { label: 'Natural language query', value: formatCellValue(row.naturalLanguageQuery) || '—' },
    { label: 'Brand', value: formatCellValue(row.brand) },
    { label: 'Region', value: formatCellValue(row.region) },
    { label: 'Platform', value: formatCellValue(row.platform) },
    { label: 'Language', value: formatCellValue(row.language) },
    { label: 'UTM', value: formatUtm(row.utmSource, row.utmMedium, row.utmCampaign) },
    { label: 'LuxPlus tier', value: formatCellValue(row.luxplusTier) },
    { label: 'Loyalty tier', value: formatCellValue(row.loyaltyTier) },
  ];
}

function buildSearchFields(
  row: SearchReproductionRow,
  placeNamesById: Map<string, PlaceNameLookup>,
): Array<SearchDetailField> {
  return [
    { label: 'Search type', value: formatCellValue(row.searchType) },
    ...buildMatchFields({
      destinations: row.destinations,
      landmarks: row.landmarks,
      properties: row.properties,
      placeNamesById,
    }),
    { label: 'Start date', value: formatCellValue(row.startDate) },
    { label: 'End date', value: formatCellValue(row.endDate) },
    { label: 'Date type', value: formatCellValue(row.dateType) },
    { label: 'Duration', value: formatCellValue(row.durationType) },
    { label: 'Flexible months', value: formatFlexibleMonths(row.flexibleMonths) },
    { label: 'Occupancy', value: formatOccupancy(row.adults, row.children) },
    { label: 'Sort', value: formatCellValue(row.sortValue) },
  ];
}

export default function ReproduceSearchPage() {
  const tenantBrand = useSelector((state: App.State) => state.tenant.brand);
  const [searchParams] = useSearchParams();
  const hasInitializedFromUrl = useRef(false);
  const [dateRange, setDateRange] = useState<DateRange<Dayjs>>(getDefaultSearchDateRange);
  const [searchId, setSearchId] = useState('');
  const [submittedParams, setSubmittedParams] = useState<ReproduceSearchParams | null>(null);

  const { data, isFetching, error, isFetched } = useReproduceSearch(submittedParams);
  const {
    data: impressionsData,
    isFetching: isImpressionsFetching,
    error: impressionsError,
    isFetched: isImpressionsFetched,
  } = useReproduceSearchImpressions(submittedParams);

  const offerIds = useMemo(() => {
    if (!impressionsData?.length) {
      return [];
    }

    return impressionsData.map((row) => row.offerId).filter((id): id is string => Boolean(id));
  }, [impressionsData]);

  const row = data ?? undefined;
  const searchBrand = (row?.brand ?? tenantBrand) as App.Tenant['brand'];
  const lookupRegion = row?.region ?? impressionsData?.[0]?.region ?? DEFAULT_OFFER_LOOKUP_REGION;
  const { offerNamesById } = useGetPublicOffersByIds(offerIds, lookupRegion, searchBrand);
  const destinationIds = row?.destinations.map((destination) => destination.id);
  const landmarkIds = row?.landmarks.map((landmark) => landmark.id);
  const placeIdsToResolve = [...(destinationIds ?? []), ...(landmarkIds ?? [])];
  const {
    places,
    placeNamesById,
    isFetching: isPlacesFetching,
    isError: isPlacesError,
  } = useGetPlaces(placeIdsToResolve, { requireGeom: true });
  const mapAreaPlaces = useMemo((): Array<PlaceGeom> => {
    return (destinationIds ?? []).flatMap((id, index) => {
      if (!isGoogleBoundsUrlValue(id)) {
        return [];
      }

      const geom = googleBoundsUrlValueToPolygonGeom(id);
      if (!geom) {
        return [];
      }

      return [
        {
          placeId: `map-area-${index}`,
          name: 'Map area',
          type: 'map_area',
          geom,
        },
      ];
    });
  }, [destinationIds]);
  const resolvedPlaceIds = useMemo(() => new Set(places.map((place) => place.placeId)), [places]);
  const propertyMapPlaces = useMemo(() => buildPropertyMapPlaces(row?.properties ?? []), [row?.properties]);
  const entityFallbackPlaces = useMemo(() => {
    if (isPlacesFetching) {
      return [];
    }

    return buildEntityFallbackPlaces([...(row?.destinations ?? []), ...(row?.landmarks ?? [])], resolvedPlaceIds);
  }, [row?.destinations, row?.landmarks, resolvedPlaceIds, isPlacesFetching]);
  const hasMapTargets =
    filterLePlaceIds(placeIdsToResolve).length > 0 ||
    mapAreaPlaces.length > 0 ||
    propertyMapPlaces.length > 0 ||
    entityFallbackPlaces.length > 0;
  const matchedPlaces = useMemo(
    () => [...places, ...mapAreaPlaces, ...propertyMapPlaces, ...entityFallbackPlaces],
    [places, mapAreaPlaces, propertyMapPlaces, entityFallbackPlaces],
  );

  const [startDate, endDate] = dateRange;
  const isInputComplete = Boolean(startDate && endDate && searchId.trim());
  const isAnyFetching = isFetching || isImpressionsFetching;

  useEffect(() => {
    if (hasInitializedFromUrl.current) {
      return;
    }

    const urlSearchId = searchParams.get('searchId')?.trim();
    const urlStartDate = searchParams.get('startDate');
    const urlEndDate = searchParams.get('endDate');

    if (!urlSearchId || !urlStartDate || !urlEndDate) {
      return;
    }

    const start = dayjs(urlStartDate);
    const end = dayjs(urlEndDate);

    if (!start.isValid() || !end.isValid()) {
      return;
    }

    hasInitializedFromUrl.current = true;

    const startOfDay = start.startOf('day');
    const endOfDay = end.endOf('day');

    setSearchId(urlSearchId);
    setDateRange([startOfDay, endOfDay]);
    setSubmittedParams(buildSubmittedParams(urlSearchId, startOfDay, endOfDay));
  }, [searchParams]);

  const handleReproduce = () => {
    if (!startDate || !endDate || !searchId.trim()) {
      return;
    }

    setSubmittedParams(buildSubmittedParams(searchId, startDate, endDate));
  };

  return (
    <Stack spacing={0}>
      <SearchBigQueryDateRangePicker dateRange={dateRange} onChange={setDateRange} dateInputWidth={DATE_INPUT_WIDTH}>
        <Box
          sx={{
            display: 'flex',
            flexGrow: 1,
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            alignItems: { md: 'center' },
          }}
        >
          <TextField
            label="Search ID"
            value={searchId}
            onChange={(event) => setSearchId(event.target.value)}
            required
            fullWidth
            size="small"
            sx={{ width: { md: SEARCH_ID_FIELD_WIDTH } }}
          />
          <Stack direction="row" spacing={1} sx={{ ml: { md: 'auto' } }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleReproduce}
              disabled={isAnyFetching || !isInputComplete}
            >
              Reproduce
            </Button>
          </Stack>
        </Box>
      </SearchBigQueryDateRangePicker>

      {error && <Alert severity="error">{error instanceof Error ? error.message : 'Failed to reproduce search'}</Alert>}

      {isFetched && !isFetching && !error && !data && (
        <Alert severity="info">No search results found for the given search id and date range.</Alert>
      )}

      {submittedParams && isFetching && <SectionLoader mt={2} />}

      {row && !isFetching && (
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={3}
          sx={{ mt: 2, width: '100%' }}
          alignItems={{ md: 'stretch' }}
        >
          <SearchDetailSection title="Meta" fields={buildMetaFields(row)} />
          <SearchDetailSection title="Search" fields={buildSearchFields(row, placeNamesById)} />
          <SearchDetailSection title="Filters" fields={buildFilterFields(row)} />
          {hasMapTargets && (
            <MatchedPlacesMap places={matchedPlaces} isFetching={isPlacesFetching} isError={isPlacesError} />
          )}
        </Stack>
      )}

      {submittedParams && (
        <Box sx={{ mt: 4 }}>
          {isImpressionsFetching && <SectionLoader />}

          {impressionsError && (
            <Alert severity="error">
              {impressionsError instanceof Error ? impressionsError.message : 'Failed to load product impressions'}
            </Alert>
          )}

          {isImpressionsFetched && !isImpressionsFetching && impressionsData?.length === 0 && (
            <Alert severity="info">No product impressions found for the given search id and date range.</Alert>
          )}

          {impressionsData && impressionsData.length > 0 && !isImpressionsFetching && (
            <ImpressionsSection impressions={impressionsData} offerNamesById={offerNamesById} />
          )}
        </Box>
      )}
    </Stack>
  );
}
